import {
  EventType,
  type MatrixClient,
  type MatrixEvent,
  type Room,
  RoomEvent,
} from 'matrix-js-sdk'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import {
  eventToMessage,
  isReplaceEvent,
} from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
// Зависимость односторонняя (subscriptions → client): history/enrichment-цепочка
// остаётся в matrixChatClient, обратных импортов оттуда быть не должно.
import {
  isPeerDeleteRequestEvent,
  readTimelineMessages,
  roomTimelineRepublishFingerprint,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'

/** Только live-события после initial/catchup sync — иначе новая вкладка флудит уже прочитанным. */
function isLiveTimelineEvent(
  toStartOfTimeline: boolean | undefined,
  data?: { liveEvent?: boolean },
): boolean {
  return !toStartOfTimeline && data?.liveEvent === true
}

function isMatrixLiveSyncState(state: string | null | undefined): boolean {
  return state === 'PREPARED' || state === 'SYNCING'
}

export interface MatrixChatUpdateEvent {
  kind: 'refresh' | 'membership' | 'room' | 'timeline'
  roomId?: string
  membership?: string
  eventType?: string
}

export interface MatrixMessageRedactionPayload {
  roomId: string
  redactedEventId: string
}

export function subscribeMatrixMessageRedactions(
  onRedaction: (payload: MatrixMessageRedactionPayload) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false
  let liveNotificationsEnabled = false

  const processEvent = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline?: boolean,
    _removed?: boolean,
    data?: { liveEvent?: boolean },
  ) => {
    if (stopped || !room || !liveNotificationsEnabled || !isLiveTimelineEvent(toStartOfTimeline, data)) {
      return
    }

    if (event.getType() !== EventType.RoomRedaction) {
      return
    }

    const redactedEventId = event.getAssociatedId()
    if (!redactedEventId) {
      return
    }

    onRedaction({ roomId: room.roomId, redactedEventId })
  }

  const onSync = (state: string) => {
    if (isMatrixLiveSyncState(state)) {
      liveNotificationsEnabled = true
    }
  }

  void startMatrixClient().then(startedClient => {
    if (stopped || !startedClient) {
      return
    }

    matrixClient = startedClient
    liveNotificationsEnabled = isMatrixLiveSyncState(matrixClient.getSyncState())

    matrixClient.on(RoomEvent.Timeline, processEvent)
    matrixClient.on('sync' as any, onSync)
  })

  return () => {
    stopped = true
    matrixClient?.removeListener(RoomEvent.Timeline, processEvent)
    matrixClient?.removeListener('sync' as any, onSync)
  }
}

export function subscribeMatrixPeerDeleteRequests(
  onDeleteRequest: (roomId: string) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false
  let liveNotificationsEnabled = false

  const processEvent = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline?: boolean,
    _removed?: boolean,
    data?: { liveEvent?: boolean },
  ) => {
    const eventType = event.getType()
    const myUserId = matrixClient?.getUserId()

    if (stopped) {
      return
    }

    if (!liveNotificationsEnabled || !isLiveTimelineEvent(toStartOfTimeline, data)) {
      return
    }

    if (!room) {
      return
    }

    if (!isPeerDeleteRequestEvent(event, myUserId)) {
      return
    }

    onDeleteRequest(room.roomId)
  }

  const onSync = (state: string) => {
    if (isMatrixLiveSyncState(state)) {
      liveNotificationsEnabled = true
    }
  }

  void startMatrixClient().then(startedClient => {
    if (stopped || !startedClient) {
      return
    }

    matrixClient = startedClient
    liveNotificationsEnabled = isMatrixLiveSyncState(matrixClient.getSyncState())

    matrixClient.on(RoomEvent.Timeline, processEvent)
    matrixClient.on('sync' as any, onSync)
  })

  return () => {
    stopped = true
    matrixClient?.removeListener(RoomEvent.Timeline, processEvent)
    matrixClient?.removeListener('sync' as any, onSync)
  }
}

export interface MatrixIncomingMessagePayload {
  roomId: string
  message: IMessage
  /** Own echo / other-device send — update preview, but no sound/unread. */
  fromSelf: boolean
}

export function subscribeMatrixIncomingMessages(
  onIncoming: (payload: MatrixIncomingMessagePayload) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false
  let liveNotificationsEnabled = false

  const onTimeline = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline?: boolean,
    _removed?: boolean,
    data?: { liveEvent?: boolean },
  ) => {
    if (stopped || !room || !liveNotificationsEnabled || !isLiveTimelineEvent(toStartOfTimeline, data)) {
      return
    }

    if (event.getType() !== EventType.RoomMessage) {
      return
    }

    const senderId = event.getSender()
    if (!senderId) {
      return
    }

    if (event.isRedacted() || isReplaceEvent(event)) {
      return
    }

    const message = eventToMessage(event)
    if (!message) {
      return
    }

    const myUserId = matrixClient?.getUserId()
    const fromSelf = Boolean(myUserId && senderId === myUserId)

    onIncoming({ roomId: room.roomId, message, fromSelf })
  }

  const onSync = (state: string) => {
    if (isMatrixLiveSyncState(state)) {
      liveNotificationsEnabled = true
    }
  }

  void startMatrixClient().then(startedClient => {
    if (stopped || !startedClient) {
      return
    }

    matrixClient = startedClient
    liveNotificationsEnabled = isMatrixLiveSyncState(matrixClient.getSyncState())

    matrixClient.on(RoomEvent.Timeline, onTimeline)
    matrixClient.on('sync' as any, onSync)
  })

  return () => {
    stopped = true
    matrixClient?.removeListener(RoomEvent.Timeline, onTimeline)
    matrixClient?.removeListener('sync' as any, onSync)
  }
}

export function subscribeMatrixChatUpdates(
  onUpdate: (event?: MatrixChatUpdateEvent) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false
  let debounceTimer: number | undefined

  const run = (event?: MatrixChatUpdateEvent) => {
    if (stopped) {
      return
    }

    window.clearTimeout(debounceTimer)
    // Чуть дольше debounce: burst Room/timeline после старта не должен мигать UI
    debounceTimer = window.setTimeout(() => onUpdate(event), 800)
  }

  const onMyMembership = (room: Room, membership: string) => {
    if (membership !== 'join' && membership !== 'invite') {
      run({
        kind: 'membership',
        roomId: room.roomId,
        membership,
      })
      return
    }

    run({
      kind: 'membership',
      roomId: room.roomId,
      membership,
    })
  }

  const onRoom = (room?: Room) => run({
    kind: 'room',
    roomId: room?.roomId,
  })

  const onTimeline = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline?: boolean,
  ) => {
    if (stopped || toStartOfTimeline) {
      return
    }

    const eventType = event.getType()
    if (
      eventType !== EventType.RoomMessage
      && eventType !== EventType.RoomName
      && eventType !== EventType.RoomTopic
      && eventType !== EventType.RoomAvatar
    ) {
      return
    }

    run({
      kind: 'timeline',
      roomId: room?.roomId,
      eventType,
    })
  }

  void startMatrixClient().then(startedClient => {
    if (stopped || !startedClient) {
      return
    }

    matrixClient = startedClient
    // Не слушаем sync PREPARED/SYNCING: long-poll раз в N секунд иначе
    // дергает полный refetch списка и «моргает» UI.
    matrixClient.on('Room' as any, onRoom)
    matrixClient.on(RoomEvent.Timeline, onTimeline)
    matrixClient.on(RoomEvent.MyMembership, onMyMembership)
  })

  return () => {
    stopped = true
    window.clearTimeout(debounceTimer)
    matrixClient?.removeListener('Room' as any, onRoom)
    matrixClient?.removeListener(RoomEvent.Timeline, onTimeline)
    matrixClient?.removeListener(RoomEvent.MyMembership, onMyMembership)
  }
}

export function subscribeMatrixRoomTimeline(
  roomId: string,
  onUpdate: (messages: IMessage[]) => void,
): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false
  let debounceTimer: number | undefined
  let publishSeq = 0
  let lastRepublishFingerprint: string | undefined
  const attachedRooms = new Set<Room>()

  const publish = (room: Room | undefined, options?: { force?: boolean }) => {
    if (stopped || !room || room.roomId !== roomId || !matrixClient) {
      return
    }

    const fingerprint = roomTimelineRepublishFingerprint(room)
    if (!options?.force && lastRepublishFingerprint === fingerprint) {
      return
    }
    lastRepublishFingerprint = fingerprint

    const seq = ++publishSeq
    const client = matrixClient
    void readTimelineMessages(client, room).then(messages => {
      if (stopped || seq !== publishSeq) {
        return
      }
      onUpdate(messages)
    })
  }

  let forceNextPublish = false

  const schedulePublish = (room: Room | undefined, options?: { force?: boolean }) => {
    if (stopped) {
      return
    }

    if (options?.force) {
      forceNextPublish = true
    }

    window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(() => {
      const force = forceNextPublish
      forceNextPublish = false
      publish(room, { force })
    }, 50)
  }

  const onTimeline = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline?: boolean,
  ) => {
    if (stopped || room?.roomId !== roomId || toStartOfTimeline) {
      return
    }

    if (
      event.getType() !== EventType.RoomMessage
      && event.getType() !== EventType.RoomMessageEncrypted
      && event.getType() !== EventType.Reaction
      && event.getType() !== EventType.RoomRedaction
    ) {
      return
    }

    // Edit (m.replace) пересобирает is_read — список читателей тоже надо сбросить.
    if (isReplaceEvent(event) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('matrix-read-receipts-updated', {
        detail: { roomId },
      }))
    }

    schedulePublish(room, {
      force: isReplaceEvent(event) || event.getType() === EventType.RoomRedaction,
    })
  }

  const onLocalEchoUpdated = (
    event: MatrixEvent,
    room: Room,
    oldEventId?: string,
    oldStatus?: unknown,
  ) => {
    if (room.roomId !== roomId) {
      return
    }

    schedulePublish(room, { force: true })
  }

  const onReceipt = (event: MatrixEvent, room: Room | undefined) => {
    if (stopped || room?.roomId !== roomId) {
      return
    }

    // Свои m.read не пересобирают таймлайн — иначе Receipt → applyRealtime → ack → Receipt.
    const myUserId = matrixClient?.getUserId()
    if (myUserId && event.getType() === 'm.receipt') {
      const content = event.getContent() as Record<string, unknown>
      const onlyOwnReceipt = Object.values(content).every(perEvent => {
        if (!perEvent || typeof perEvent !== 'object') {
          return false
        }
        const read = (perEvent as Record<string, unknown>)['m.read']
          ?? (perEvent as Record<string, unknown>)['m.read.private']
        if (!read || typeof read !== 'object') {
          return false
        }
        const userIds = Object.keys(read as Record<string, unknown>)
        return userIds.length > 0 && userIds.every(userId => userId === myUserId)
      })
      if (onlyOwnReceipt) {
        return
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('matrix-read-receipts-updated', {
        detail: { roomId },
      }))
    }

    schedulePublish(room, { force: true })
  }

  const attachRoom = (room: Room) => {
    if (attachedRooms.has(room)) {
      return
    }

    attachedRooms.add(room)
    room.on(RoomEvent.Timeline, onTimeline)
    room.on(RoomEvent.LocalEchoUpdated, onLocalEchoUpdated)
    room.on(RoomEvent.Receipt, onReceipt)
    // История уже подгружается в fetchMessages; здесь только live-хвост из sync.
    publish(room)
  }

  const onRoom = (room: Room) => {
    if (room.roomId === roomId) {
      attachRoom(room)
    }
  }

  void startMatrixClient().then(startedClient => {
    if (stopped || !startedClient) {
      return
    }

    matrixClient = startedClient
    const room = matrixClient.getRoom(roomId)
    if (room) {
      attachRoom(room)
    }

    matrixClient.on(RoomEvent.Timeline, onTimeline)
    matrixClient.on('Room' as any, onRoom)
  })

  return () => {
    stopped = true
    window.clearTimeout(debounceTimer)
    attachedRooms.forEach(room => {
      room.removeListener(RoomEvent.Timeline, onTimeline)
      room.removeListener(RoomEvent.LocalEchoUpdated, onLocalEchoUpdated)
      room.removeListener(RoomEvent.Receipt, onReceipt)
    })
    attachedRooms.clear()
    matrixClient?.removeListener(RoomEvent.Timeline, onTimeline)
    matrixClient?.removeListener('Room' as any, onRoom)
  }
}
